import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, FileText, CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Public application form modal.
 * Submits multipart/form-data to /api/jobs/{id}/apply.
 */
export default function ApplyModal({ job, onClose, onSubmitted }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [coverNote, setCoverNote] = useState("");
  const [answers, setAnswers] = useState({}); // { [question_id]: text }
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Lock scroll + ESC to close
  useEffect(() => {
    if (!job) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [job, onClose, submitting]);

  // Reset state whenever a new job is opened
  useEffect(() => {
    if (job) {
      setName("");
      setEmail("");
      setPhone("");
      setCoverNote("");
      setAnswers({});
      setResume(null);
      setSuccess(false);
    }
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!job) return null;

  const screening = Array.isArray(job.screening_questions) ? job.screening_questions : [];

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Resume must be a PDF.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Resume must be 5 MB or smaller.");
      return;
    }
    setResume(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      toast.error("Please attach your resume (PDF).");
      return;
    }
    // Validate required screening answers
    for (const q of screening) {
      if (q.required && !(answers[q.id] || "").trim()) {
        toast.error(`"${q.label}" is required.`);
        return;
      }
    }

    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("email", email.trim());
    fd.append("phone", phone.trim());
    fd.append("cover_note", coverNote.trim());
    fd.append(
      "answers",
      JSON.stringify(
        screening.map((q) => ({
          question_id: q.id,
          label: q.label,
          answer: (answers[q.id] || "").trim(),
        })),
      ),
    );
    fd.append("resume", resume);

    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/apply`, fd);
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      data-testid="apply-modal-overlay"
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 md:py-12"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
      style={{ animation: "am-fade 220ms ease-out both" }}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-modal-title"
        data-testid="apply-modal"
        className="relative bg-bg-secondary border border-steel-dark w-full max-w-2xl max-h-[92vh] flex flex-col"
        style={{ animation: "am-rise 320ms cubic-bezier(.16,.84,.34,1) both" }}
      >
        <div className="h-[3px] bg-signal" />

        {/* Header */}
        <div className="flex items-start justify-between p-6 md:p-8 pb-4 border-b border-steel-dark">
          <div className="flex-1 min-w-0 pr-6">
            <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-2">
              Apply for
            </div>
            <h2
              id="apply-modal-title"
              className="heading text-xl md:text-2xl text-white uppercase leading-tight"
            >
              {job.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="apply-modal-close"
            aria-label="Close"
            className="flex-shrink-0 -mr-2 -mt-2 p-2 text-steel-light hover:text-signal disabled:opacity-40 transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        {success ? (
          <div className="flex-1 flex items-center justify-center p-10 text-center">
            <div>
              <CheckCircle2 className="text-signal mx-auto mb-5" size={48} strokeWidth={1.5} />
              <h3 className="heading text-2xl text-white uppercase mb-3">
                Application sent.
              </h3>
              <p className="text-steel-light text-sm md:text-base leading-relaxed max-w-md mx-auto mb-8">
                Thanks {name.split(" ")[0] || "for applying"}! We've received your details and resume.
                We'll review and reach out if there's a fit.
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="apply-modal-done"
                className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="apply-name"
                  className="apply-input"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email *">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="apply-email"
                  className="apply-input"
                  placeholder="you@example.com"
                />
              </Field>
            </div>

            <Field label="Phone *">
              <input
                type="tel"
                required
                maxLength={40}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="apply-phone"
                className="apply-input"
                placeholder="(403) 555-0199"
              />
            </Field>

            <Field label="Cover Note (optional)">
              <textarea
                rows={4}
                maxLength={4000}
                value={coverNote}
                onChange={(e) => setCoverNote(e.target.value)}
                data-testid="apply-cover-note"
                className="apply-input resize-none"
                placeholder="Tell us briefly why you're a good fit, your availability, and any relevant experience."
              />
            </Field>

            {screening.length > 0 && (
              <div className="space-y-5 pt-2 border-t border-steel-dark">
                <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase pt-3">
                  A few quick questions
                </div>
                {screening.map((q) => {
                  const isYesNo = q.type === "yes_no" || q.type === "yes_no_text";
                  const yesNoVal =
                    q.type === "yes_no_text"
                      ? answers[`${q.id}__yn`]
                      : answers[q.id];
                  const setYesNo = (v) => {
                    if (q.type === "yes_no_text") {
                      const followup = answers[`${q.id}__txt`] || "";
                      setAnswers({
                        ...answers,
                        [`${q.id}__yn`]: v,
                        [q.id]: v === "Yes" && followup ? `Yes — ${followup}` : v,
                      });
                    } else {
                      setAnswers({ ...answers, [q.id]: v });
                    }
                  };
                  return (
                    <div key={q.id} className="block">
                      <div className="mb-2.5 text-white text-sm md:text-base leading-snug">
                        {q.label}
                        {q.required && <span className="text-signal ml-1">*</span>}
                      </div>
                      {isYesNo ? (
                        <>
                          <div
                            role="radiogroup"
                            aria-label={q.label}
                            className="grid grid-cols-2 gap-3"
                          >
                            {["Yes", "No"].map((opt) => {
                              const selected = yesNoVal === opt;
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  role="radio"
                                  aria-checked={selected}
                                  onClick={() => setYesNo(opt)}
                                  data-testid={`apply-q-${q.id}-${opt.toLowerCase()}`}
                                  className={`font-mono text-xs tracking-[0.2em] uppercase border-2 px-5 py-3 transition-colors ${
                                    selected
                                      ? "bg-signal border-signal text-white"
                                      : "bg-bg-primary/40 border-steel-dark text-steel-light hover:border-signal/60 hover:text-white"
                                  }`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                          {q.type === "yes_no_text" && yesNoVal === "Yes" && (
                            <div className="mt-3" style={{ animation: "am-fade 220ms ease-out both" }}>
                              <div className="mb-1.5 text-steel-light text-xs leading-snug">
                                List any you hold:
                              </div>
                              <input
                                type="text"
                                maxLength={300}
                                value={answers[`${q.id}__txt`] || ""}
                                onChange={(e) => {
                                  const txt = e.target.value;
                                  setAnswers({
                                    ...answers,
                                    [`${q.id}__txt`]: txt,
                                    [q.id]: txt ? `Yes — ${txt}` : "Yes",
                                  });
                                }}
                                data-testid={`apply-q-${q.id}-text`}
                                className="apply-input"
                                placeholder="e.g. Fall protection, Ubiquiti UBWA, Network+, A+"
                                autoFocus
                              />
                            </div>
                          )}
                        </>
                      ) : q.type === "long" ? (
                        <textarea
                          rows={3}
                          required={!!q.required}
                          maxLength={2000}
                          value={answers[q.id] || ""}
                          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                          data-testid={`apply-q-${q.id}`}
                          className="apply-input resize-none"
                        />
                      ) : (
                        <input
                          type="text"
                          required={!!q.required}
                          maxLength={300}
                          value={answers[q.id] || ""}
                          onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                          data-testid={`apply-q-${q.id}`}
                          className="apply-input"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Resume dropzone */}
            <div className="pt-2 border-t border-steel-dark">
              <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase pt-3 mb-1.5">
                Final Step
              </div>
              <div className="mb-2.5 text-white text-base md:text-lg leading-snug">
                Attach your resume <span className="text-signal">*</span>
              </div>
              <p className="text-steel-light text-xs md:text-sm leading-relaxed mb-3">
                PDF only · 5 MB max. Drag and drop or browse from your device.
              </p>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFile(e.dataTransfer.files?.[0]);
                }}
                data-testid="apply-resume-drop"
                className={`cursor-pointer border-2 border-dashed transition-colors p-6 text-center ${
                  resume
                    ? "border-signal/60 bg-signal/5"
                    : "border-steel-dark hover:border-signal/60 bg-bg-primary/40"
                }`}
              >
                {resume ? (
                  <div className="flex items-center justify-center gap-3 text-white">
                    <FileText className="text-signal" size={20} />
                    <div className="text-left">
                      <div className="text-sm font-semibold truncate max-w-[260px]">
                        {resume.name}
                      </div>
                      <div className="font-mono text-[10px] tracking-[0.2em] text-steel-light uppercase">
                        {(resume.size / 1024).toFixed(0)} KB · click to replace
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-steel-light">
                    <Upload className="mx-auto mb-2 text-signal" size={22} />
                    <div className="text-sm">
                      Drop your resume here, or{" "}
                      <span className="text-signal underline underline-offset-4">browse</span>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] mt-1 uppercase">
                      PDF only · 5 MB max
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="apply-resume-input"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2 border-t border-steel-dark">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal disabled:opacity-50 px-5 py-3 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit"
                className="inline-flex items-center justify-center gap-2 bg-signal hover:bg-signal-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        @keyframes am-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes am-rise {
          from { opacity: 0; transform: translateY(20px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        .apply-input {
          width: 100%;
          background-color: #050505;
          color: #fff;
          border: 1px solid #2A2E33;
          padding: 0.85rem 1rem;
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
        }
        .apply-input:focus {
          outline: none;
          border-color: #E84A1A;
        }
      `}</style>
    </div>,
    document.body,
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-2 font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}
